/* eslint-disable array-callback-return */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { PinsDiscountLegend } from '../PinsDiscountLegend';
import { DetailsPlayer } from '../Team';
import { DetailsPlayerSubmit } from '../Team/DetailsPlayer';
import { ModalWhatsPins } from '../Team/ModalPinsF11';

import { Button, Modal, NavBar, PlayerList, SliderOver } from '@/components';
import useHostbackButton from '@/hooks/useHostbackButton';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import usePinsAccount from '@/hooks/usePinsAccount';
import { usePlayerTemplateMap } from '@/hooks/usePlayer';
import { useTeamStore } from '@/hooks/useTeamStore';
import { useTournament } from '@/hooks/useTournament';
import { TeamActionTypes } from '@/store';
import {
  EnumTeamContext,
  Player,
  PlayerClub,
  PlayerHistoric,
} from '@/types/team';
import { PlayerF5, PlayerMyTeamF5 } from '@/types/teamf5';
import { cn, EMPTY_TEMPLATE_PLAYER, isF5 } from '@/utils';

export const PlayerListTransfer = ({
  context = EnumTeamContext.Team11,
  handleTransfer,
}: {
  context?: EnumTeamContext;
  handleTransfer: (data: DetailsPlayerSubmit, pin?: string) => void;
}) => {
  const { pinsData } = usePinsAccount();
  const { state, dispatch } = useTeamStore();
  const { isDesktop, isMobile } = useIsDesktop();
  const playersMaps = usePlayerTemplateMap();
  const { tournamentMap } = useTournament();
  const tournamentStatusMap = tournamentMap?.tournamentStatusMap;
  const navigate = useNavigate();

  const { pathname } = useLocation();

  const [stateComponent, setStateComponent] = useState<
    | PlayerClub[]
    | PlayerHistoric[]
    | Player[]
    | PlayerF5[]
    | PlayerMyTeamF5[]
    | undefined
  >(undefined);

  const contextF5 = useMemo((): boolean => {
    return isF5(context);
  }, [context]);

  useEffect(() => {
    if (contextF5) {
      state.teamF5 && setStateComponent(state.teamF5);
    } else {
      state.team && setStateComponent(state.team);
    }
  }, [contextF5, state.teamF5, state.team]);

  const handleCloseBack = useCallback(() => {
    dispatch({
      type: TeamActionTypes.SET_STATE,
      payload: {
        showPlayersListTransfer: false,
        selectedPlayerOnList: null,
        playerDetailsModalOpen: false,
        ...(!state.showPlayersList && { selectedPlayerOnTeam: null }),
      },
    });
  }, [dispatch, state.showPlayersList]);

  const handleCloseButton = useCallback(() => {
    handleCloseBack();
    navigate(pathname, { replace: true });
  }, [handleCloseBack, navigate, pathname]);

  useHostbackButton(handleCloseBack);

  const onHandleTransfer = useCallback(
    (data: DetailsPlayerSubmit) => {
      handleTransfer(data);
    },
    [handleTransfer]
  );

  const mainContent = useMemo(() => {
    return (
      <>
        <div className="flex flex-col lg:px-10 relative">
          <p className="hidden lg:block text-sm font-bold text-black pt-6 pb-4 pl-3">
            Seleccioná al jugador saliente de tu equipo{' '}
          </p>
          <div className="items-center flex justify-between pr-[23px] lg:pr-[10px] min-h-[40px] border-b border-gdt-gray-7 pl-[16px] bg-white">
            <p className="text-sm text-gdt-blue-dark font-bold lg:text-gdt-gray-2 lg:text-xs">
              Jugadores
            </p>
            <div className="flex justify-between items-center text-3xs text-gdt-gray-4 font-medium max-w-[100px] lg:max-w-none">
              <p className="text-center flex-1 lg:w-[90px]">
                {isDesktop ? 'Equipos GDT' : 'Equipos GDT'}
              </p>
              <p className="text-center flex-1 lg:w-[90px]">
                {isDesktop ? 'Puntos Prom.' : 'Ptos. Prom.'}
              </p>
            </div>
          </div>
          <div
            className={cn(
              'flex-1 overflow-auto lg:max-h-[456px] pb-[92px] lg:pb-10'
            )}
          >
            {stateComponent?.map((player) => {
              const playerByPosition =
                playersMaps.get(player?.id) || EMPTY_TEMPLATE_PLAYER;

              const team = tournamentMap?.tournamentTeamsMap
                ? tournamentMap?.tournamentTeamsMap.get(playerByPosition.c)
                : undefined;

              const status = tournamentStatusMap
                ? tournamentStatusMap.get(playerByPosition?.e)
                : undefined;

              if (player)
                return (
                  <PlayerList.Player
                    key={playerByPosition.id}
                    checked={player.id === state.selectedPlayerOnTeam?.id}
                    className="border-b border-gdt-gray-7"
                    disabled={false}
                    initialCheckState={false}
                    isCaptain={player.capitan}
                    isInsured={player.seguro}
                    isRadio={true}
                    isSubstitute={false}
                    nameInput={`${playerByPosition.id}`}
                    player={playerByPosition}
                    radioName="field-transfer"
                    team={team?.nombre ?? ''}
                    teamId={team?.id ?? 0}
                    tournamentStatus={status}
                    variant="selectable"
                    onInputChanges={() => {
                      dispatch({
                        type: TeamActionTypes.SET_STATE,
                        payload: {
                          selectedPlayerOnTeam: playerByPosition,
                        },
                      });
                    }}
                  />
                );
            })}
          </div>
          <div className="px-4 pb-6 pt-6 lg:text-center fixed bottom-0 left-0 w-full bg-white">
            <Button
              className="w-full lg:w-60"
              disabled={Boolean(!state.selectedPlayerOnTeam?.id)}
              type="button"
              variant="primary"
              onClick={() => {
                dispatch({
                  type: TeamActionTypes.SET_STATE,
                  payload: {
                    playerDetailsModalOpen: true,
                  },
                });
              }}
            >
              Confirmar transferencia
            </Button>
          </div>
        </div>
        <Modal
          bottomSheetClassName="px-[16px]"
          className="px-[1.06rem] lg:px-11 py-10"
          controlledOnClose={() => {
            dispatch({
              type: TeamActionTypes.SET_STATE,
              payload: {
                playerDetailsModalOpen: false,
              },
            });
          }}
          isControlledOpen={Boolean(state.playerDetailsModalOpen)}
          render={() => {
            if (state.selectedPlayerOnList?.id) {
              return (
                <DetailsPlayer
                  messageBeforeActions={
                    ((state.responseTeamF11?.estado &&
                      state.responseTeamF11?.seLeDescontara1Pin) ||
                      (state.responseTeamF5?.estado &&
                        state.responseTeamF5?.seLeDescontara1Pin)) &&
                    state.selectedPlayerOnList && <PinsDiscountLegend />
                  }
                  playerId={state.selectedPlayerOnList?.id}
                  selectedPlayerOnField={state.selectedPlayerOnTeam}
                  selectedPlayerOnList={state.selectedPlayerOnList}
                  variant={'add'}
                  onSubmitPlayer={(data) => {
                    onHandleTransfer(data);
                  }}
                />
              );
            }

            return null;
          }}
          size="xs"
          variant="controlled"
        />
        <ModalWhatsPins
          showModal={
            (pinsData.showModalWhatsPins || pinsData.showInputsModalPins) &&
            Boolean(state.showPlayersListTransfer)
          }
          onSubmitTransfer={onHandleTransfer}
        />
        ;
      </>
    );
  }, [
    isDesktop,
    stateComponent,
    state.selectedPlayerOnTeam,
    state.playerDetailsModalOpen,
    state.showPlayersListTransfer,
    state.selectedPlayerOnList,
    pinsData.showModalWhatsPins,
    pinsData.showInputsModalPins,
    onHandleTransfer,
    playersMaps,
    tournamentMap?.tournamentTeamsMap,
    tournamentStatusMap,
    dispatch,
    state.responseTeamF11,
    state.responseTeamF5,
  ]);

  // RENDER MOBILE
  if (isMobile) {
    return (
      <SliderOver
        contentClassName="flex flex-col"
        open={Boolean(state.showPlayersListTransfer)}
      >
        <NavBar
          mobile={{
            leftContent: {
              backAction: handleCloseButton,
            },
            centerContent: {
              title: 'Seleccioná al jugador saliente de tu equipo',
            },
          }}
        >
          {mainContent}
        </NavBar>
      </SliderOver>
    );
  }

  // RENDER DESKTOP
  return (
    state.showPlayersListTransfer && (
      <Modal
        className="px-0 pb-8 pt-2"
        controlledOnClose={handleCloseButton}
        isControlledOpen={Boolean(state.showPlayersListTransfer)}
        render={() => {
          return mainContent;
        }}
        size="xs"
        variant="controlled"
      />
    )
  );
};
